import BigNumber from "bignumber.js";

const MIN_MARGIN = 0.05;

export const getLiquidationPrice = ({
  direction,
  baseAmount,
  quoteAmount,
}: {
  direction: string | undefined;
  baseAmount: BigNumber | undefined;
  quoteAmount: BigNumber | undefined;
}) => {
  if (!quoteAmount || !direction || !baseAmount || baseAmount.isZero())
    return undefined;

  switch (direction.toLowerCase()) {
    case "short":
      return quoteAmount.times(1 - MIN_MARGIN).div(baseAmount);

    case "long":
      return quoteAmount.div(baseAmount.times(1 - MIN_MARGIN));

    default:
      return undefined;
  }
};

export const getLeverage = ({
  direction,
  baseAmount,
  quoteAmount,
  basePrice,
}: {
  direction: string | undefined;
  baseAmount: BigNumber | undefined;
  quoteAmount: BigNumber | undefined;
  basePrice: BigNumber | undefined;
}) => {
  if (!quoteAmount || !direction || !baseAmount || !basePrice) return undefined;

  if (direction === "Long") {
    return baseAmount
      .times(basePrice)
      .div(baseAmount.times(basePrice).minus(quoteAmount))
      .abs();
  }

  if (direction === "Short") {
    return quoteAmount
      .div(quoteAmount.minus(baseAmount.times(basePrice)))
      .abs();
  }

  return undefined;
};

export const getSafeAmountByLeverage = ({
  direction,
  baseAmount,
  quoteAmount,
  leverage,
  basePrice,
}: {
  direction: string | undefined;
  baseAmount: BigNumber | undefined;
  quoteAmount: BigNumber | undefined;
  basePrice: BigNumber | undefined;
  leverage: number | undefined;
}) => {
  if (!leverage || !direction || !baseAmount || !quoteAmount || !basePrice)
    return undefined;

  if (direction === "Long") {
    return quoteAmount
      .times(leverage)
      .div(1 - leverage)
      .abs();
  }

  if (direction === "Short") {
    return baseAmount
      .times(basePrice)
      .times(leverage)
      .div(1 - leverage)
      .abs();
  }

  return undefined;
};

export const getSafeLeverage = (leverage: number): number => 0.8 * leverage;

export const getIsCritialLeverage = (
  leverage?: BigNumber | number,
  maxLeverage?: number,
): boolean =>
  !!leverage &&
  !!maxLeverage &&
  BigNumber(leverage)?.gt(getSafeLeverage(maxLeverage));

const MIN_SAFE_FRACTION = BigNumber(1e6);

export const fixPrecision = (bn: BigNumber) => {
  let pow = 0;

  while (MIN_SAFE_FRACTION.pow(pow + 1).gte(bn)) {
    pow++;
  }

  return {
    digits: pow * 6,
    val: MIN_SAFE_FRACTION.pow(-pow).times(bn).toNumber(),
  };
};
